import { useMemo } from 'react';
import useVersionManagementV1 from './useVersionManagementV1';
import { extractFieldValidations } from '../utils/fieldValidationParser';

/**
 * Custom hook to manage field validation versions
 * Stores and tracks different versions of field validations from regenerations
 *
 * Uses useVersionManagementV1 which fixes:
 * - Local edit detection via isLocalUpdateRef
 * - Proper version tracking without creating duplicates on edit
 */
const useFieldValidationVersions = (conversation, isStreaming, streamingContent, isLocalUpdateRef = null) => {
  // Configuration for field validation version management
  const validationConfig = {
    // Determine if a message contains field validations
    isRelevantMessage: (msg) => {
      const content = msg.content || '';
      return content.includes('**Name:**') ||
             content.includes('**Type:**') ||
             content.includes('#### Validation Rules') ||
             content.includes('### Validation Rules') ||
             content.includes('**Validation Rules:**');
    },

    // Parse field validations from content
    // Always use extractFieldValidations() because the backend's generic section parser
    // produces {title, content, category} objects, but the right panel needs field-specific
    // objects with {name, label, type, validations, group, implementation}
    parseContent: (content, msg) => {
      // Always use extractFieldValidations() — the backend's generic section parser
      // produces {title, content, category} but the right panel needs field-specific
      // objects with {name, label, type, validations, group, implementation}.
      // extractFieldValidations() handles both raw markdown and section content.
      const { fields } = extractFieldValidations(content || msg?.content || '');
      return fields;
    },

    // Extract version information from a message
    // Note: The id will be set by useVersionManagement based on messageIndex
    // We return null for id here to let the parent hook set it properly
    extractVersions: (msg, parsedFields, versionNumber, isPreview = false) => {
      return {
        id: null, // Will be overwritten by useVersionManagementV1 with messageIndex-based ID
        timestamp: msg.createdAt || new Date().toISOString(),
        fields: parsedFields,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },

    conversation,
    isStreaming,
    streamingContent,
    isLocalUpdateRef
  };

  // Use the V1 version management hook that supports isLocalUpdateRef
  const versionData = useVersionManagementV1(validationConfig);

  // Memoize the currentVersion to prevent unnecessary re-renders
  // Only recreate when the actual version data changes
  const currentVersion = useMemo(() => {
    if (!versionData.currentVersion) return null;
    return {
      ...versionData.currentVersion,
      fields: versionData.currentVersion.parsedContent || versionData.currentVersion.fields
    };
  }, [versionData.currentVersion]);

  // Map the generic version data to field validation-specific structure
  return {
    versions: versionData.versions,
    currentVersion,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useFieldValidationVersions;
